/**
 * /producao/atrasos — Análise de Atrasos (Ishikawa enriquecida, Sprint P4).
 *
 * DOIS eixos no mesmo painel (D12): lente TEMPO (atraso em minutos por
 * categoria 6M) e lente QUALIDADE (FPY + Pareto de defeito, NCs da
 * Conferência). Mesma infra visual, fontes distintas — o tabchip de lente
 * troca a série, não o componente.
 *
 * 3 zonas:
 *   A — EntityHeader com subtítulo DINÂMICO (ecoa recorte + lente) +
 *       filterbar sticky (fase → etapa encadeada, categoria, responsável,
 *       fornecedor [só lente tempo — C7], pedido, período). TODO filtro
 *       refaz a chamada ao endpoint.
 *   B — KpiStrip por lente + Fishbone SVG ponderado (6 espinhas SEMPRE) +
 *       Pareto 80/20 com toggle de granularidade. Cross-filtering: clicar
 *       em espinha/barra/linha de ranking adiciona o filtro (toggle).
 *   C — Tendência (barras empilhadas 6M + linha total + média), rankings
 *       2×2, custo + MTTR por causa, top ofensores e tabela-fonte paginada.
 *
 * Regras duras: nenhuma métrica inventada (null → "—" com tooltip);
 * fishbone sem peso = regressão; célula de categoria/timestamp nunca vazia.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import EntityHeader from "../../components/EntityHeader";
import ExportMenu from "../../components/ExportMenu";
import KpiStrip from "../../components/KpiStrip";
import { useAuth } from "../../context/AuthContext";
import { formatarReais } from "../../services/financeiro";
import {
  CATALOGO_ETAPAS_INFO,
  CATEGORIAS_6M,
  ETAPAS_POR_FASE,
  ETAPA_LABELS,
  FASES_PAINEL,
  FASE_INFO,
  analiseAtrasos,
} from "../../services/producaoV2";
import {
  formatarDataCurta,
  formatarDataHoraCurta,
  formatarHorasMinutos,
  formatarMinutosAtraso,
} from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SearchInput,
  SecondaryButton,
  SectionTitle,
  Select,
} from "../Orcamentos/styles";
import { COR_CATEGORIA, LABEL_CATEGORIA, ORDEM_CATEGORIAS } from "./categorias6m";
import Fishbone from "./_atrasos/Fishbone";
import { exportarAnalise } from "./_atrasos/exportarAnalise";
import { CustoBloco, ParetoBloco, TendenciaBloco } from "./_atrasos/graficos";
import {
  BarraFerramentas,
  LinhaErro,
  Mono,
  NotaDiscreta,
  Shimmer,
  TabelaFixa,
  TituloBloco,
  fmtNum,
  fmtPct,
} from "./_shared/ui";

const PERIODOS = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "custom", label: "Custom" },
];

const FILTROS_INICIAIS = {
  fase: "",
  etapa: "",
  categoria: "",
  responsavel: "",
  fornecedor: "",
};

const DEBOUNCE_PEDIDO_MS = 400;

/** {fase interna: código} — cross-filter de ranking por etapa seta a fase
 * junto (o select de Etapa é encadeado à Fase). */
const FASE_POR_ETAPA = CATALOGO_ETAPAS_INFO.reduce((acc, e) => {
  acc[e.codigo] = e.fase;
  return acc;
}, {});
const CODIGO_POR_LABEL_ETAPA = Object.entries(ETAPA_LABELS).reduce(
  (acc, [codigo, label]) => {
    acc[label] = codigo;
    return acc;
  },
  {},
);

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const FiltroBarra = styled.div`
  position: sticky;
  top: 0;
  z-index: 6;
  background: var(--bg);
  border-bottom: 1px solid var(--border-subtle);
  padding: 8px 0 10px;
  margin-bottom: 14px;
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const CampoFiltro = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 130px;
  flex: 1 1 130px;
  max-width: 210px;

  .lbl {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-3);
  }
`;

const InputData = styled.input`
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.82rem;
  font-family: var(--font-mono);

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const BotaoGhost = styled.button`
  border: none;
  background: none;
  color: var(--accent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    background: var(--accent-soft);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const GridDuasColunas = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const GridRankings = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const NomeComBarra = styled.div`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .prop {
    position: absolute;
    left: 0;
    top: 2px;
    bottom: 2px;
    background: var(--surface-3);
    border-radius: 4px;
  }
  .txt {
    position: relative;
  }
`;

const LegendaFishbone = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 6px;

  .item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: var(--ink-2);
  }
  .dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
  }
`;

const CabecalhoBloco = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

const DotCat = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  margin-right: 2px;
`;

const RodapePaginacao = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 10px;
`;

// ---------------------------------------------------------------------------
// Pedaços de UI
// ---------------------------------------------------------------------------

function TabChips({ opcoes, valor, onChange, ariaLabel }) {
  return (
    <div className="tabchip-group" role="tablist" aria-label={ariaLabel}>
      {opcoes.map((o) => (
        <button
          key={o.id}
          type="button"
          role="tab"
          aria-selected={valor === o.id}
          className={`tabchip${valor === o.id ? " active" : ""}`}
          style={{
            border: "none",
            background: valor === o.id ? undefined : "transparent",
          }}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PillCategoria({ codigo }) {
  if (!codigo) return <span style={{ color: "var(--ink-4)" }}>—</span>;
  const label = LABEL_CATEGORIA[codigo] || codigo;
  return (
    <Pill $tone="neutral" title={label}>
      <DotCat style={{ background: COR_CATEGORIA[codigo] }} />
      {label}
    </Pill>
  );
}

function BadgeEmAberto() {
  return (
    <Pill $tone="warning" title="Sem timestamp de resolução no registro">
      Em aberto
    </Pill>
  );
}

function RankingMini({ titulo, linhas, ehTempo, textoVazio, onLinhaClick }) {
  const visiveis = (linhas || []).slice(0, 8);
  return (
    <Card style={{ padding: 14 }}>
      <TituloBloco>{titulo}</TituloBloco>
      {visiveis.length === 0 ? (
        <NotaDiscreta style={{ margin: 0 }}>{textoVazio}</NotaDiscreta>
      ) : (
        <TabelaFixa>
          <colgroup>
            <col style={{ width: ehTempo ? "52%" : "56%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: ehTempo ? "13%" : "22%" }} />
            {ehTempo && <col style={{ width: "13%" }} />}
          </colgroup>
          <thead>
            <tr>
              <th>Nome</th>
              <th>{ehTempo ? "Peso" : "Ocorr."}</th>
              <th>%</th>
              {ehTempo && <th>Ocorr.</th>}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((linha) => (
              <tr
                key={linha.nome}
                data-clicavel={onLinhaClick ? "true" : undefined}
                onClick={onLinhaClick ? () => onLinhaClick(linha) : undefined}
                title={
                  onLinhaClick
                    ? `${linha.nome} — clique para filtrar`
                    : linha.nome
                }
              >
                <td>
                  <NomeComBarra>
                    <span
                      className="prop"
                      style={{ width: `${Math.min(linha.pct || 0, 100)}%` }}
                    />
                    <span className="txt">{linha.nome}</span>
                  </NomeComBarra>
                </td>
                <td>
                  <Mono>
                    {ehTempo
                      ? formatarHorasMinutos(linha.total_min, "0min")
                      : linha.ocorrencias}
                  </Mono>
                </td>
                <td>
                  <Mono>{fmtPct(linha.pct)}</Mono>
                </td>
                {ehTempo && (
                  <td>
                    <Mono>{linha.ocorrencias}</Mono>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </TabelaFixa>
      )}
    </Card>
  );
}

function TopOfensores({ registros, ehTempo }) {
  const agrupar = (chave) => {
    const mapa = new Map();
    (registros || []).forEach((r) => {
      const id =
        chave === "ambiente"
          ? r.ambiente_numero || "—"
          : r.pedido_numero || "—";
      const nome =
        chave === "ambiente"
          ? `${r.ambiente_numero || "?"} · ${r.ambiente_nome || ""}`
          : r.pedido_numero || "—";
      const item = mapa.get(id) || { id, nome, peso: 0, ocorrencias: 0 };
      item.peso += ehTempo ? Number(r.minutos) || 0 : 1;
      item.ocorrencias += 1;
      mapa.set(id, item);
    });
    return [...mapa.values()].sort((a, b) => b.peso - a.peso).slice(0, 5);
  };

  const blocos = [
    { titulo: "Top ambientes", itens: agrupar("ambiente") },
    { titulo: "Top pedidos", itens: agrupar("pedido") },
  ];

  return (
    <GridDuasColunas>
      {blocos.map((bloco) => (
        <Card key={bloco.titulo} style={{ padding: 14 }}>
          <TituloBloco>{bloco.titulo}</TituloBloco>
          {bloco.itens.length === 0 ? (
            <NotaDiscreta style={{ margin: 0 }}>
              Sem ocorrências no recorte.
            </NotaDiscreta>
          ) : (
            bloco.itens.map((item) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "5px 0",
                  borderBottom: "1px solid var(--border-subtle)",
                  fontSize: 12.5,
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={item.nome}
                >
                  {item.nome}
                </span>
                <span style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                  <Pill $tone="danger">
                    <Mono>
                      {ehTempo
                        ? formatarHorasMinutos(item.peso, "0min")
                        : item.peso}
                    </Mono>
                  </Pill>
                  <Mono style={{ color: "var(--ink-3)", fontSize: 11 }}>
                    {item.ocorrencias} ocorr.
                  </Mono>
                </span>
              </div>
            ))
          )}
          <NotaDiscreta>derivado da página atual da tabela-fonte.</NotaDiscreta>
        </Card>
      ))}
    </GridDuasColunas>
  );
}

// ---------------------------------------------------------------------------
// Tabela-fonte (C5) — colunas por lente, larguras somando 100%
// ---------------------------------------------------------------------------

function TabelaFonteTempo({ registros, onAbrirAmbiente }) {
  return (
    <TabelaFixa>
      <colgroup>
        {["9%", "11%", "6%", "8%", "6%", "9%", "8%", "8%", "8%", "7%", "6%", "8%", "6%"].map(
          (w, i) => (
            <col key={i} style={{ width: w }} />
          ),
        )}
      </colgroup>
      <thead>
        <tr>
          <th>Pedido</th>
          <th>Ambiente</th>
          <th>Fase</th>
          <th>Etapa</th>
          <th>Posto</th>
          <th>Responsável</th>
          <th>Categoria</th>
          <th>Causa</th>
          <th>Fornecedor</th>
          <th>Atraso</th>
          <th>Custo</th>
          <th>Aberto em</th>
          <th>Resolvido</th>
        </tr>
      </thead>
      <tbody>
        {registros.map((r) => (
          <tr
            key={r.id}
            data-clicavel={r.ambiente_id ? "true" : undefined}
            onClick={r.ambiente_id ? () => onAbrirAmbiente(r.ambiente_id) : undefined}
          >
            <td>
              <Mono>{r.pedido_numero}</Mono>
            </td>
            <td title={`${r.ambiente_numero} · ${r.ambiente_nome}`}>
              {r.ambiente_nome || r.ambiente_numero}
            </td>
            <td title={FASE_INFO[r.fase]?.rotulo || ""}>
              {r.fase ? (
                <Pill $tone="purple">{FASE_INFO[r.fase]?.curto || r.fase}</Pill>
              ) : (
                "—"
              )}
            </td>
            <td title={r.etapa?.label || "sem etapa vinculada"}>
              {r.etapa?.label || "—"}
            </td>
            <td title={r.posto ? `${r.posto.nome} (${r.posto.numero})` : ""}>
              {r.posto ? r.posto.nome : "—"}
            </td>
            <td title={r.responsavel_nome || ""}>{r.responsavel_nome || "—"}</td>
            <td>
              <PillCategoria codigo={r.categoria} />
            </td>
            <td title={r.descricao || ""}>{r.descricao || "—"}</td>
            <td title={r.fornecedor_nome || "sem fornecedor vinculado"}>
              {r.fornecedor_nome || "—"}
            </td>
            <td>
              <Mono style={{ color: "var(--danger-text)" }}>
                {formatarMinutosAtraso(r.minutos)}
              </Mono>
            </td>
            <td title={formatarReais(r.custo_rs)}>
              <Mono style={{ color: "var(--danger-text)" }}>
                {formatarReais(r.custo_rs)}
              </Mono>
            </td>
            <td title={formatarDataHoraCurta(r.registrado_em)}>
              <Mono>{formatarDataHoraCurta(r.registrado_em)}</Mono>
            </td>
            <td>
              {r.resolvido_em ? (
                <Mono>{formatarDataHoraCurta(r.resolvido_em)}</Mono>
              ) : (
                <BadgeEmAberto />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </TabelaFixa>
  );
}

function TabelaFonteQualidade({ registros, onAbrirAmbiente }) {
  return (
    <TabelaFixa>
      <colgroup>
        {["10%", "12%", "13%", "9%", "11%", "10%", "10%", "9%", "9%", "7%"].map(
          (w, i) => (
            <col key={i} style={{ width: w }} />
          ),
        )}
      </colgroup>
      <thead>
        <tr>
          <th>Pedido</th>
          <th>Peça</th>
          <th>Ambiente</th>
          <th>Posto</th>
          <th>Operador</th>
          <th>Categoria</th>
          <th>Tipo defeito</th>
          <th>Boa/Refugo</th>
          <th>Registrado</th>
          <th>Resolvido</th>
        </tr>
      </thead>
      <tbody>
        {registros.map((r, idx) => (
          <tr
            key={`${r.evento_id}-${idx}`}
            data-clicavel={r.ambiente_id ? "true" : undefined}
            onClick={r.ambiente_id ? () => onAbrirAmbiente(r.ambiente_id) : undefined}
          >
            <td>
              <Mono>{r.pedido_numero}</Mono>
            </td>
            <td title={`${r.peca_numero} · ${r.peca_descricao || ""}`}>
              <Mono>{r.peca_numero}</Mono>
            </td>
            <td title={`${r.ambiente_numero || ""} · ${r.ambiente_nome || ""}`}>
              {r.ambiente_nome || r.ambiente_numero || "—"}
            </td>
            <td title={r.posto ? `${r.posto.nome} (${r.posto.numero})` : ""}>
              {r.posto ? r.posto.nome : "—"}
            </td>
            <td title={r.operador_nome || ""}>{r.operador_nome || "—"}</td>
            <td>
              <PillCategoria codigo={r.categoria} />
            </td>
            <td title={r.descricao || r.tipo || ""}>{r.tipo || "—"}</td>
            <td>
              <Mono>
                {r.qtd_boa ?? 0}/{r.qtd_refugo ?? 0}
              </Mono>
            </td>
            <td title={formatarDataHoraCurta(r.registrado_em)}>
              <Mono>{formatarDataHoraCurta(r.registrado_em)}</Mono>
            </td>
            <td>
              {r.resolvido_em ? (
                <Mono>{formatarDataHoraCurta(r.resolvido_em)}</Mono>
              ) : (
                <BadgeEmAberto />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </TabelaFixa>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function AnaliseAtrasos() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [lente, setLente] = useState("tempo");
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [pedidoInput, setPedidoInput] = useState("");
  const [pedidoDebounced, setPedidoDebounced] = useState("");
  const [periodo, setPeriodo] = useState("30d");
  const [custom, setCustom] = useState({ inicio: "", fim: "" });
  const [pagina, setPagina] = useState(1);
  const [granularidade, setGranularidade] = useState("categoria");

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [usuarios, setUsuarios] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [parceiros, setParceiros] = useState([]);

  const refPareto = useRef(null);

  const ehTempo = lente === "tempo";

  // ---- opções do filtro Responsável (tempo=Usuario · qualidade=Operador) ----
  // Sem a lista (ex.: sem permissão usuario:view/operador:view), o select
  // fica desabilitado com tooltip — os demais filtros seguem funcionando.
  useEffect(() => {
    const extrairLista = (p) =>
      Array.isArray(p) ? p : p?.data || p?.results || [];
    request("/api/usuarios?pageSize=100", { silentError: true })
      .then((p) => setUsuarios(extrairLista(p)))
      .catch(() => {});
    request("/api/operadores", { silentError: true })
      .then((p) => setOperadores(extrairLista(p)))
      .catch(() => {});
    // C7 — filtro Fornecedor (Parceiro): só a lente tempo captura o dado.
    request("/api/parceiros?ativo=true", { silentError: true })
      .then((p) => setParceiros(extrairLista(p)))
      .catch(() => {});
  }, [request]);

  // ---- debounce do filtro Pedido ----
  useEffect(() => {
    const t = setTimeout(
      () => setPedidoDebounced(pedidoInput.trim()),
      DEBOUNCE_PEDIDO_MS,
    );
    return () => clearTimeout(t);
  }, [pedidoInput]);

  const customValido =
    periodo !== "custom"
    || (Boolean(custom.inicio) && Boolean(custom.fim) && custom.fim >= custom.inicio);

  // ---- carga (TODO filtro refaz a chamada — estado único do recorte) ----
  const carregar = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      const resposta = await analiseAtrasos(request, {
        lente,
        fase: filtros.fase || undefined,
        etapa: filtros.etapa || undefined,
        categoria: filtros.categoria || undefined,
        responsavel: filtros.responsavel || undefined,
        fornecedor: filtros.fornecedor || undefined,
        pedido: pedidoDebounced || undefined,
        periodo,
        inicio: periodo === "custom" ? custom.inicio : undefined,
        fim: periodo === "custom" ? custom.fim : undefined,
        pagina,
      });
      setDados(resposta);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar as ocorrências.");
    } finally {
      setCarregando(false);
    }
  }, [request, lente, filtros, pedidoDebounced, periodo, custom, pagina]);

  useEffect(() => {
    if (!customValido) return;
    carregar();
  }, [carregar, customValido]);

  // Filtro novo volta pra página 1 (no mount é no-op).
  useEffect(() => {
    setPagina(1);
  }, [lente, filtros, pedidoDebounced, periodo, custom]);

  const rolarAte = useCallback((ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // ---- cross-filters (toggle) ----
  const alternarCategoria = useCallback((codigo) => {
    setFiltros((f) => ({
      ...f,
      categoria: f.categoria === codigo ? "" : codigo,
    }));
  }, []);

  const aoClicarEspinha = useCallback(
    (codigo) => {
      alternarCategoria(codigo);
      rolarAte(refPareto);
    },
    [alternarCategoria, rolarAte],
  );

  const aoClicarRankingEtapa = useCallback((linha) => {
    const codigo = CODIGO_POR_LABEL_ETAPA[linha.nome];
    if (!codigo) return;
    setFiltros((f) =>
      f.etapa === codigo
        ? { ...f, etapa: "", fase: "" }
        : { ...f, etapa: codigo, fase: FASE_POR_ETAPA[codigo] || "" },
    );
  }, []);

  const aoClicarBucket = useCallback((inicioIso, bucket) => {
    const fim = new Date(`${inicioIso}T00:00:00`);
    fim.setDate(fim.getDate() + (bucket === "semana" ? 6 : 0));
    const fimIso = `${fim.getFullYear()}-${String(fim.getMonth() + 1).padStart(2, "0")}-${String(fim.getDate()).padStart(2, "0")}`;
    setCustom({ inicio: inicioIso, fim: fimIso });
    setPeriodo("custom");
  }, []);

  // C7 — cross-filter do ranking "Por fornecedor" (toggle): o payload traz
  // só o nome; o id vem do lookup na lista de parceiros (no-op sem match,
  // mesmo padrão do ranking por etapa).
  const aoClicarRankingFornecedor = useCallback(
    (linha) => {
      const parceiro = parceiros.find((p) => p.nome === linha.nome);
      if (!parceiro) return;
      const id = String(parceiro.id);
      setFiltros((f) => ({ ...f, fornecedor: f.fornecedor === id ? "" : id }));
    },
    [parceiros],
  );

  const trocarLente = (novaLente) => {
    if (novaLente === lente) return;
    setLente(novaLente);
    setGranularidade("categoria");
    // Responsável muda de entidade entre lentes (Usuario ↔ OperadorPin);
    // fornecedor só existe na lente tempo (C7).
    setFiltros((f) => ({ ...f, responsavel: "", fornecedor: "" }));
  };

  const filtrosAtivos =
    ["fase", "etapa", "categoria", "responsavel", "fornecedor"]
      .filter((c) => filtros[c]).length
    + (pedidoDebounced ? 1 : 0)
    + (periodo !== "30d" ? 1 : 0);

  const limparFiltros = () => {
    setFiltros(FILTROS_INICIAIS);
    setPedidoInput("");
    setPeriodo("30d");
    setCustom({ inicio: "", fim: "" });
  };

  // ---- derivações do payload ----
  const kpis = dados?.kpis;
  const totalOcorrencias = ehTempo
    ? kpis?.ocorrencias ?? 0
    : kpis?.nao_conformidades ?? 0;
  const recorteVazio = Boolean(dados) && totalOcorrencias === 0;

  const rotuloPeriodo = useMemo(() => {
    if (periodo === "custom" && custom.inicio && custom.fim) {
      return `${formatarDataCurta(custom.inicio)} a ${formatarDataCurta(custom.fim)}`;
    }
    return `últimos ${periodo.replace("d", "")} dias`;
  }, [periodo, custom]);

  const subtitulo = useMemo(() => {
    const partes = [];
    if (filtros.fase) partes.push(FASE_INFO[filtros.fase]?.rotulo || filtros.fase);
    if (filtros.etapa) partes.push(ETAPA_LABELS[filtros.etapa] || filtros.etapa);
    if (filtros.categoria) partes.push(LABEL_CATEGORIA[filtros.categoria]);
    if (filtros.fornecedor) {
      const parceiro = parceiros.find(
        (p) => String(p.id) === String(filtros.fornecedor),
      );
      partes.push(parceiro ? parceiro.nome : "fornecedor");
    }
    partes.push(rotuloPeriodo);
    if (kpis) {
      if (ehTempo) {
        partes.push(
          `${formatarHorasMinutos(kpis.total_min, "0min")} de atraso em ${kpis.ocorrencias} ocorrência(s)`,
        );
      } else {
        partes.push(`FPY ${fmtPct(kpis.fpy_pct)}`);
        partes.push(`${kpis.nao_conformidades} não-conformidade(s)`);
      }
    }
    return partes.join(" · ");
  }, [filtros, parceiros, rotuloPeriodo, kpis, ehTempo]);

  const itensKpi = useMemo(() => {
    if (!kpis) return [];
    if (ehTempo) {
      return [
        {
          label: "Atraso total",
          value: formatarHorasMinutos(kpis.total_min, "0min"),
          sub: `${kpis.ocorrencias} ocorrência(s) no recorte`,
        },
        {
          label: "Ocorrências",
          value: kpis.ocorrencias,
          sub: "registros de atraso (causa 6M)",
        },
        {
          label: "Atraso médio/ocorrência",
          value: kpis.medio_min === null ? "—" : `${fmtNum(kpis.medio_min)} min`,
          sub: "Σ minutos ÷ ocorrências",
        },
        {
          label: "Custo estimado",
          value: formatarReais(kpis.custo_rs),
          sub: `custo-hora R$ ${fmtNum(dados?.parametros?.custo_hora ?? 0, 2)}`,
        },
        {
          label: "MTTR global",
          value: "—",
          sub: <span title={kpis.mttr_obs}>sem dado de resolução</span>,
        },
      ];
    }
    const fpy = kpis.fpy_pct;
    const corFpy =
      fpy === null
        ? "var(--ink-3)"
        : fpy >= 95
          ? "var(--success-text)"
          : fpy >= 90
            ? "var(--warning-text)"
            : "var(--danger-text)";
    return [
      {
        label: "FPY global",
        value: fpy === null ? "—" : fmtPct(fpy),
        sub: (
          <span style={{ color: corFpy }} title={fpy === null ? "sem peças apresentadas no recorte" : undefined}>
            {kpis.nao_conformidades} não-conformidade(s)
          </span>
        ),
      },
      {
        label: "Taxa de retrabalho",
        value: kpis.taxa_retrabalho_pct === null ? "—" : fmtPct(kpis.taxa_retrabalho_pct),
        sub: `${kpis.pecas_refugadas ?? 0} de ${kpis.pecas_apresentadas ?? 0} peça(s)`,
      },
      {
        label: "Custo de retrabalho",
        value: formatarReais(kpis.custo_retrabalho_rs),
        sub:
          Number(kpis.ncs_nao_estimadas) > 0
            ? `${kpis.ncs_nao_estimadas} NC(s) sem categoria — não estimadas`
            : "horas estimadas × custo-hora",
      },
      {
        label: "Defeito mais frequente",
        value: kpis.defeito_mais_frequente?.tipo || "—",
        sub: kpis.defeito_mais_frequente
          ? `${kpis.defeito_mais_frequente.ocorrencias} ocorrência(s)`
          : "sem defeitos tipados no recorte",
      },
      {
        label: "MTTR de disposição",
        value: "—",
        sub: <span title={kpis.mttr_obs}>sem dado de resolução</span>,
      },
    ];
  }, [kpis, ehTempo, dados]);

  const chavePeso = ehTempo ? "total_min" : "ocorrencias";

  const categoriasFishbone = useMemo(() => {
    const cats = dados?.fishbone?.categorias || [];
    return ORDEM_CATEGORIAS.map((codigo) => {
      const c = cats.find((x) => x.categoria === codigo) || {};
      return {
        codigo,
        label: LABEL_CATEGORIA[codigo],
        peso: Number(c[chavePeso] || 0),
        pct: Number(c.pct || 0),
        subCausas: (c.sub_causas || []).map((s) =>
          ehTempo
            ? {
                rotulo: `${s.etapa_label ? `${s.etapa_label}: ` : ""}${s.descricao || "(sem descrição)"}`,
                peso: Number(s.total_min || 0),
              }
            : {
                rotulo: s.tipo || "(sem tipo)",
                peso: Number(s.ocorrencias || 0),
              },
        ),
      };
    });
  }, [dados, chavePeso, ehTempo]);

  const formatPeso = useCallback(
    (peso) => (ehTempo ? `${fmtNum(peso, 0)} min` : fmtNum(peso, 0)),
    [ehTempo],
  );

  const itensPareto = useMemo(() => {
    if (!dados) return [];
    if (granularidade === "categoria") {
      return (dados.pareto?.por_categoria || []).map((l) => ({
        nome: l.label || LABEL_CATEGORIA[l.categoria] || l.categoria,
        categoria: l.categoria,
        peso: Number(l[chavePeso] || 0),
        pct: l.pct,
        pct_acumulado: l.pct_acumulado,
      }));
    }
    const fonte = ehTempo ? dados.pareto?.por_causa : dados.pareto?.por_tipo;
    return (fonte || []).map((l) => ({
      nome: ehTempo
        ? `${l.etapa_label ? `${l.etapa_label}: ` : ""}${l.descricao || "(sem descrição)"}`
        : l.tipo || "(sem tipo)",
      categoria: l.categoria,
      peso: Number(l[chavePeso] || 0),
      pct: l.pct,
      pct_acumulado: l.pct_acumulado,
    }));
  }, [dados, granularidade, chavePeso, ehTempo]);

  const rotuloCabecaFishbone = useMemo(() => {
    const recorte = filtros.fase
      ? FASE_INFO[filtros.fase]?.curto || filtros.fase
      : "Geral";
    const per = periodo === "custom" ? "custom" : periodo;
    return `${ehTempo ? "Atrasos" : "NCs"} · ${recorte} · ${per}`;
  }, [ehTempo, filtros.fase, periodo]);

  const registros = dados?.tabela_fonte?.registros || [];
  const totalRegistros = dados?.tabela_fonte?.total || 0;
  const porPagina = dados?.tabela_fonte?.por_pagina || 50;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / porPagina));

  const rankings = dados?.rankings || {};
  const responsaveis = ehTempo ? usuarios : operadores;

  return (
    <PageContainer>
      {/* sticky fica com a filterbar (evita duas âncoras em top:0). */}
      <EntityHeader
        sticky={false}
        onBack={() => navigate("/producao")}
        backLabel="Produção"
        title="Análise de Atrasos"
        subtitle={subtitulo || "Ishikawa ponderado + Pareto de causas"}
      />

      {/* -------- Zona A — filterbar sticky (recorta TODOS os blocos) ----- */}
      <FiltroBarra>
        <CampoFiltro>
          <span className="lbl">Fase</span>
          <Select
            value={filtros.fase}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, fase: e.target.value, etapa: "" }))
            }
          >
            <option value="">Todas</option>
            {FASES_PAINEL.map((f) => (
              <option key={f.codigo} value={f.codigo}>
                {f.label}
              </option>
            ))}
          </Select>
        </CampoFiltro>
        <CampoFiltro>
          <span className="lbl">Etapa</span>
          <Select
            value={filtros.etapa}
            disabled={!filtros.fase}
            title={!filtros.fase ? "Selecione a fase primeiro" : undefined}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, etapa: e.target.value }))
            }
          >
            <option value="">
              {filtros.fase ? "Todas" : "Selecione a fase"}
            </option>
            {(ETAPAS_POR_FASE[filtros.fase] || []).map((e) => (
              <option key={e.codigo} value={e.codigo}>
                {e.label}
              </option>
            ))}
          </Select>
        </CampoFiltro>
        <CampoFiltro>
          <span className="lbl">Categoria (6M)</span>
          <Select
            value={filtros.categoria}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, categoria: e.target.value }))
            }
          >
            <option value="">Todas</option>
            {CATEGORIAS_6M.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.label}
              </option>
            ))}
          </Select>
        </CampoFiltro>
        <CampoFiltro>
          <span className="lbl">
            {ehTempo ? "Responsável" : "Operador (Conferência)"}
          </span>
          <Select
            value={filtros.responsavel}
            disabled={responsaveis.length === 0}
            title={
              responsaveis.length === 0
                ? "Lista indisponível (sem registros ou sem permissão de visualização)"
                : undefined
            }
            onChange={(e) =>
              setFiltros((f) => ({ ...f, responsavel: e.target.value }))
            }
          >
            <option value="">Todos</option>
            {responsaveis.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome || r.email || r.id}
              </option>
            ))}
          </Select>
        </CampoFiltro>
        <CampoFiltro>
          <span className="lbl">Fornecedor</span>
          <Select
            value={filtros.fornecedor}
            disabled={!ehTempo || parceiros.length === 0}
            title={
              !ehTempo
                ? "Disponível na lente Atraso (tempo) — a ocorrência de qualidade não vincula fornecedor"
                : parceiros.length === 0
                  ? "Lista indisponível (sem parceiros ativos ou sem permissão de visualização)"
                  : undefined
            }
            onChange={(e) =>
              setFiltros((f) => ({ ...f, fornecedor: e.target.value }))
            }
          >
            <option value="">Todos</option>
            {parceiros.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </Select>
        </CampoFiltro>
        <CampoFiltro>
          <span className="lbl">Pedido</span>
          <SearchInput
            type="search"
            placeholder="PD-UN1-N…"
            value={pedidoInput}
            onChange={(e) => setPedidoInput(e.target.value)}
            aria-label="Filtrar por número do pedido"
          />
        </CampoFiltro>
        <CampoFiltro style={{ maxWidth: "none", flex: "0 0 auto" }}>
          <span className="lbl">Período</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <TabChips
              opcoes={PERIODOS}
              valor={periodo}
              onChange={setPeriodo}
              ariaLabel="Período de análise"
            />
            {periodo === "custom" && (
              <>
                <InputData
                  type="date"
                  value={custom.inicio}
                  max={custom.fim || undefined}
                  onChange={(e) =>
                    setCustom((c) => ({ ...c, inicio: e.target.value }))
                  }
                  aria-label="Início do período"
                />
                <InputData
                  type="date"
                  value={custom.fim}
                  min={custom.inicio || undefined}
                  onChange={(e) =>
                    setCustom((c) => ({ ...c, fim: e.target.value }))
                  }
                  aria-label="Fim do período"
                />
              </>
            )}
          </div>
        </CampoFiltro>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginLeft: "auto",
            paddingBottom: 2,
          }}
        >
          {filtrosAtivos > 0 && (
            <Pill $tone="neutral">{filtrosAtivos} filtro(s)</Pill>
          )}
          <BotaoGhost type="button" onClick={limparFiltros}>
            Limpar filtros
          </BotaoGhost>
          <ExportMenu
            disabled={!dados}
            onExport={(formato) => dados && exportarAnalise(formato, dados)}
          />
        </div>
      </FiltroBarra>

      {periodo === "custom" && !customValido && (
        <NotaDiscreta style={{ margin: "0 0 12px" }}>
          Informe início e fim (fim não pode ser anterior ao início) para
          aplicar o período custom.
        </NotaDiscreta>
      )}

      {/* -------- Zona B — lente + KPIs + fishbone + Pareto -------- */}
      <BarraFerramentas style={{ justifyContent: "flex-start" }}>
        <TabChips
          ariaLabel="Lente de análise"
          valor={lente}
          onChange={trocarLente}
          opcoes={[
            { id: "tempo", label: "⏱ Atraso (min)" },
            { id: "qualidade", label: "✓ Qualidade (FPY/defeitos)" },
          ]}
        />
      </BarraFerramentas>

      {erro && (
        <LinhaErro style={{ marginBottom: 14 }}>
          <ErrorMsg style={{ flex: 1 }}>
            Não foi possível carregar as ocorrências. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {!dados && carregando ? (
        <>
          <Shimmer $h={92} style={{ marginBottom: 16 }} />
          <Shimmer $h={360} style={{ marginBottom: 16 }} />
          <Shimmer $h={260} />
        </>
      ) : dados ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            opacity: carregando ? 0.6 : 1,
            transition: "opacity 0.15s ease",
          }}
        >
          <KpiStrip items={itensKpi} />

          <Card>
            <CabecalhoBloco>
              <SectionTitle>
                Ishikawa ponderado —{" "}
                {ehTempo ? "atraso por categoria" : "NCs por categoria"}
              </SectionTitle>
              {filtros.categoria && (
                <BotaoGhost
                  type="button"
                  onClick={() => alternarCategoria(filtros.categoria)}
                >
                  filtro: {LABEL_CATEGORIA[filtros.categoria]} — limpar
                </BotaoGhost>
              )}
            </CabecalhoBloco>
            <Fishbone
              categorias={categoriasFishbone}
              rotuloCabeca={rotuloCabecaFishbone}
              formatPeso={formatPeso}
              categoriaAtiva={filtros.categoria || null}
              onCategoriaClick={aoClicarEspinha}
            />
            <LegendaFishbone>
              {categoriasFishbone.map((c) => (
                <span className="item" key={c.codigo}>
                  <span
                    className="dot"
                    style={{ background: COR_CATEGORIA[c.codigo] }}
                  />
                  {c.label}{" "}
                  <Mono style={{ color: "var(--ink-3)" }}>
                    {formatPeso(c.peso)}
                  </Mono>
                </span>
              ))}
            </LegendaFishbone>
            {recorteVazio && (
              <EmptyMsg>
                Nenhuma ocorrência no recorte — ajuste os filtros ou amplie o
                período.{" "}
                <BotaoGhost type="button" onClick={limparFiltros}>
                  Limpar filtros
                </BotaoGhost>
              </EmptyMsg>
            )}
          </Card>

          <Card ref={refPareto}>
            <CabecalhoBloco>
              <SectionTitle>Pareto 80/20</SectionTitle>
              <TabChips
                ariaLabel="Granularidade do Pareto"
                valor={granularidade}
                onChange={setGranularidade}
                opcoes={[
                  { id: "categoria", label: "Categoria" },
                  {
                    id: "detalhe",
                    label: ehTempo ? "Causa específica" : "Tipo de defeito",
                  },
                ]}
              />
            </CabecalhoBloco>
            <ParetoBloco
              itens={itensPareto}
              ehTempo={ehTempo}
              granularidade={granularidade}
              categoriaAtiva={filtros.categoria || null}
              onCategoriaClick={alternarCategoria}
            />
          </Card>

          {/* -------- Zona C -------- */}
          <Card>
            <SectionTitle>Tendência temporal</SectionTitle>
            <TendenciaBloco
              tendencia={dados.tendencia}
              periodo={dados.periodo}
              ehTempo={ehTempo}
              onBucketClick={aoClicarBucket}
            />
            <NotaDiscreta>
              barras empilhadas pelas 6 categorias · linha = total por{" "}
              {dados.tendencia?.bucket === "semana" ? "semana" : "dia"} ·
              clique num bucket para focar o período.
            </NotaDiscreta>
          </Card>

          <GridRankings>
            <RankingMini
              titulo="Por etapa"
              linhas={rankings.por_etapa}
              ehTempo={ehTempo}
              textoVazio="Sem ocorrências com etapa vinculada no recorte."
              onLinhaClick={aoClicarRankingEtapa}
            />
            <RankingMini
              titulo="Por posto"
              linhas={rankings.por_posto}
              ehTempo={ehTempo}
              textoVazio="Sem ocorrências com posto identificado no recorte."
            />
            <RankingMini
              titulo="Por responsável"
              linhas={rankings.por_responsavel}
              ehTempo={ehTempo}
              textoVazio="Sem responsável identificado no recorte."
            />
            <RankingMini
              titulo="Por fornecedor"
              linhas={rankings.por_fornecedor}
              ehTempo={ehTempo}
              textoVazio={
                rankings.por_fornecedor_obs
                || "Sem terceiros no recorte."
              }
              onLinhaClick={ehTempo ? aoClicarRankingFornecedor : undefined}
            />
          </GridRankings>

          <GridDuasColunas>
            <Card>
              <SectionTitle>Custo por categoria</SectionTitle>
              <CustoBloco
                custoPorCategoria={dados.custo_por_categoria}
                ehTempo={ehTempo}
                parametros={dados.parametros}
              />
            </Card>
            <Card>
              <SectionTitle>MTTR por causa</SectionTitle>
              <TabelaFixa>
                <colgroup>
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "19%" }} />
                  <col style={{ width: "19%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Categoria</th>
                    <th>MTTR</th>
                    <th>Resolvidas</th>
                    <th>Em aberto</th>
                  </tr>
                </thead>
                <tbody>
                  {(dados.custo_por_categoria || []).map((c) => (
                    <tr key={c.categoria}>
                      <td>
                        <PillCategoria codigo={c.categoria} />
                      </td>
                      <td>
                        {c.mttr_min === null ? (
                          <Pill $tone="neutral" title={kpis?.mttr_obs}>
                            sem dado de resolução
                          </Pill>
                        ) : (
                          <Mono>{formatarMinutosAtraso(c.mttr_min)}</Mono>
                        )}
                      </td>
                      <td>
                        <Mono>{c.resolvidas ?? "—"}</Mono>
                      </td>
                      <td>
                        <Mono>{c.em_aberto ?? "—"}</Mono>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TabelaFixa>
              <NotaDiscreta>
                MTTR entra quando o registro ganhar timestamp de resolução
                (gestão de disposição — P5).
              </NotaDiscreta>
            </Card>
          </GridDuasColunas>

          <TopOfensores registros={registros} ehTempo={ehTempo} />

          <Card>
            <CabecalhoBloco>
              <SectionTitle>
                Tabela-fonte{" "}
                <Mono style={{ fontSize: 13, color: "var(--ink-3)" }}>
                  ({totalRegistros})
                </Mono>
              </SectionTitle>
            </CabecalhoBloco>
            {registros.length === 0 ? (
              <EmptyMsg>Nenhuma ocorrência no recorte atual.</EmptyMsg>
            ) : ehTempo ? (
              <TabelaFonteTempo
                registros={registros}
                onAbrirAmbiente={(id) => navigate(`/producao/ambientes/${id}`)}
              />
            ) : (
              <TabelaFonteQualidade
                registros={registros}
                onAbrirAmbiente={(id) => navigate(`/producao/ambientes/${id}`)}
              />
            )}
            {totalPaginas > 1 && (
              <RodapePaginacao>
                <Mono style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  Página {dados.tabela_fonte?.pagina ?? pagina} de {totalPaginas}
                </Mono>
                <div style={{ display: "flex", gap: 8 }}>
                  <SecondaryButton
                    type="button"
                    disabled={pagina <= 1}
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </SecondaryButton>
                  <SecondaryButton
                    type="button"
                    disabled={pagina >= totalPaginas}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Próxima
                  </SecondaryButton>
                </div>
              </RodapePaginacao>
            )}
          </Card>
        </div>
      ) : null}
    </PageContainer>
  );
}
